// src/components/brand/useSurfaceTone.ts
import { useEffect, useState } from 'react';

import type { EdgeLexMarkTone } from './edgeLexMarkAssets';

const readTone = (): EdgeLexMarkTone =>
  typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme-mode') === 'light'
    ? 'light'
    : 'dark';

/**
 * The resolved theme, read from the `data-theme-mode` attribute that the
 * pre-paint script in index.html sets before first paint and that
 * `applyThemeModeToDocument` keeps current.
 *
 * Reading the DOM rather than the preferences store means this also works on
 * the pre-authentication screens, and cannot drift from the resolution logic
 * that already owns the attribute. Dark is the fallback, matching the
 * pre-paint script's own default.
 */
export function useSurfaceTone(): EdgeLexMarkTone {
  const [tone, setTone] = useState(readTone);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    setTone(readTone());
    const observer = new MutationObserver(() => setTone(readTone()));
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme-mode'] });
    return () => observer.disconnect();
  }, []);

  return tone;
}
